import { Component, Input, OnInit, OnDestroy, Optional, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import {
	ISearchVinResult,
	IVehicle,
	IVehicleDialogData,
	IVehicleMakesDataEntity,
	IVehicleMakesResult,
	IVehicleModelsDataEntity,
	IVehicleModelsResult,
	IVehicleTrimsDataEntity,
	IVehicleTrimsResult,
	IVinInformation
} from 'src/app/core/services/mobile-api/vehicle-api/vehicle-api.model';
import { LoanApplicationService } from 'src/app/core/services/loan-application/loan-application.service';
import { VehicleApiService } from 'src/app/core/services/mobile-api/vehicle-api/vehicle-api.service';
import { numberOnlyValidator } from 'src/app/shared/validators/form-validators';
import { CONFIRMED_MILEAGE, MAX_YEAR } from 'src/app/shared/constants/common-const';
import { ILoanApplication } from 'src/app/core/services/loan-application/loan-application.model';
import { ProductOfferDetailsUtils } from 'src/app/core/services/loan-application/product-offer/product-offer-details/product-offer-details-utils';
import { RoutingPathsEnum, RoutingService } from 'src/app/core/services/routing/routing.service';
import { preferNotAnswerVehicleStatus } from 'src/app/core/services/loan-application/product-offer/product/product.model';
import { Router } from '@angular/router';

@Component({
	selector: 'op-vehicle-form',
	templateUrl: './vehicle-form.component.html',
	styleUrls: ['./vehicle-form.component.scss']
})
export class VehicleFormComponent implements OnInit, OnDestroy {
	@Input() vinResults: IVinInformation;
	@Input() vendorName: string = null;
	@Input() vendorVehicleId: number = null;
	@Input() inValidVehicleTrimIds: number[];
	@Input() inValidVehicleModelIds: number[];
	@Input() inValidVehicleMakeIds: number[];

	formGroup: FormGroup;
	years: number[];
	makes: IVehicleMakesDataEntity[];
	models: IVehicleModelsDataEntity[];
	trims: IVehicleTrimsDataEntity[];
	hasMultipleValidProductOffers: boolean;
	productOfferDetailUtilities: ProductOfferDetailsUtils;
	private subscription = new Subscription();
	inValidVehicle: boolean = false;
	hasSPLOnly: boolean;

	constructor(
		private formBuilder: FormBuilder,
		private translocoService: TranslocoService,
		private loanAppService: LoanApplicationService,
		private vehicleApiService: VehicleApiService,
		private routingService: RoutingService,
		@Optional() private dialogRef: MatDialogRef<VehicleFormComponent>,
		@Optional() @Inject(MAT_DIALOG_DATA) public dialogData: IVehicleDialogData,
		private router: Router
	) {
		this.createForm(this.formBuilder);
	}

	ngOnInit(): void {
		const loanAppSub = this.loanAppService.loanApplication$.pipe(filter(Boolean)).subscribe({
			next: (loanApp: ILoanApplication) => {
				this.productOfferDetailUtilities = ProductOfferDetailsUtils.fromLoanApp(loanApp);
				this.hasMultipleValidProductOffers = this.productOfferDetailUtilities.hasMultipleValidProductOffers();
				this.hasSPLOnly = this.productOfferDetailUtilities.hasOnlySPL();
			}
		});
		this.subscription.add(loanAppSub);
		const vinInfo = this.dialogData.vinResults ? this.dialogData : this.vinResults;
		if (this.dialogData.vinResults) {
			this.vendorName = this.dialogData.vendorName;
			this.vendorVehicleId = this.dialogData.vendorVehicleId;
		}
		this.vinResults = vinInfo;
		if (vinInfo) {
			const { vinResults } = vinInfo;
			const { year, makeId, modelId, trims } = vinResults.data;
			this.getVehicleInformation(year, makeId, modelId, trims);
		}
		if (this.dialogRef) {
			this.getIneligibleRules();
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	createForm(fb: FormBuilder): void {
		this.formGroup = fb.group({
			year: [{ value: '', disabled: true }],
			makeId: [{ value: '', disabled: true }],
			modelId: [{ value: '', disabled: true }],
			trimId: [{ value: '', disabled: true }],
			makeStr: [{ value: '', disabled: true }],
			modelStr: [{ value: '', disabled: true }],
			trimStr: [{ value: '', disabled: true }],
			odometer: ['', [Validators.required, numberOnlyValidator()]]
		});
	}

	onSubmit(): void {
		const formValue = this.formGroup.getRawValue();
		const payload = {
			...formValue,
			vehicleOwnershipStatus: 'OWN_PAID_OFF_VEHICLE',
			applicantVehicleTitleSalvaged: 'NO',
			isFinalVehicle: true,
			source: this.vendorName,
			vendorVehicleId: this.vendorVehicleId
		};
		if (this.vinResults?.vin) {
			payload.vin = this.vinResults.vin;
		}
		if (this.vinResults?.vinResults['licensePlate']) {
			payload.licensePlate = this.vinResults.vinResults['licensePlate'];
		}
		this.addCustomValidators();

		this.vehicleApiService.setVehicle(payload, this.loanAppService.loanApplicationId).subscribe({
			next: (response) => this.handleSuccess(response, payload),
			error: (error) => this.handleError(error, payload)
		});
	}

	private handleSuccess(response: any, payload: any): void {
		this.inValidVehicle = false;
		if (!response) {
			this.closeDialog(payload);
			return;
		}
		this.routingService.route(RoutingPathsEnum.vehicleEligibility);
		this.closeDialog(payload);
	}

	private handleError(error: any, payload: any): void {
		if (error.status === 400 && error?.error[0]?.msg.startsWith('vehicle')) {
			this.inValidVehicle = true;
		} else if (!this.hasMultipleValidProductOffers && error?.error[0]?.msg === 'loanApplication.ineligible.vehicle') {
			this.inValidVehicle = false;
			this.routingService.route(RoutingPathsEnum.status);
		} else {
			this.inValidVehicle = false;
			this.routingService.route(RoutingPathsEnum.vehicleEligibility);
		}
		this.closeDialog(payload);
	}

	private closeDialog(payload?: any): void {
		if (this.dialogRef && !this.inValidVehicle) {
			this.dialogRef.close(payload);
		}
	}

	returnToOffers = function () {
		this.inValidVehicle = false;
		let vehicle: IVehicle = { isFinalVehicle: true, vehicleOwnershipStatus: preferNotAnswerVehicleStatus };
		this.vehicleApiService.setVehicle(vehicle, this.loanAppService.loanApplicationId).subscribe({
			next: (rsp) => this.routingService.route(RoutingPathsEnum.offerStatus),
			error: (err) => this.routingService.route(RoutingPathsEnum.offerStatus)
		});
		this.closeDialog();
	};

	reloadPage(): void {
		this.inValidVehicle = false;
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		this.router.onSameUrlNavigation = 'reload';
		this.router.navigate([this.router?.url]);
		this.closeDialog();
	}

	private getIneligibleRules(): void {
		this.vehicleApiService.getIneligibleVehicleRules().subscribe({
			next: (data) => {
				this.inValidVehicleMakeIds = data.inValidVehicleMakeIds;
				this.inValidVehicleModelIds = data.inValidVehicleModelIds;
				this.inValidVehicleTrimIds = data.inValidVehicleTrimIds;
			}
		});
	}

	getVehicleInformation(year: number, makeId: number, modelId: number, trims: any[]): void {
		forkJoin({
			makes: this.getVehicleMakes(year),
			models: this.getVehicleModels(year, makeId),
			trims: this.getVehicleTrims(year, makeId, modelId)
		}).subscribe({
			next: (rsp) => {
				const makeStr = rsp.makes.data.find((make) => make.id === makeId)?.makeName || '';
				const modelStr = rsp.models.data.find((model) => model.id === modelId)?.modelName || '';
				const trimStr = rsp.trims.data.find((trim) => trim.id === trims[0]?.id)?.trimName || '';
				this.formGroup.patchValue({
					year,
					makeId,
					modelId,
					trimId: trims[0]?.id,
					makeStr,
					modelStr,
					trimStr
				});
			}
		});
	}

	getVehicleMakes(year: number): Observable<IVehicleMakesResult> {
		return this.vehicleApiService.getVehicleMakes(year).pipe(
			tap((rsp) => {
				this.makes = rsp.data;
			})
		);
	}

	getVehicleModels(year: number, makeId: number): Observable<IVehicleModelsResult> {
		return this.vehicleApiService.getVehicleModels(year, makeId).pipe(
			tap((rsp) => {
				this.models = rsp.data;
			})
		);
	}

	getVehicleTrims(year: number, makeId: number, modelId: number): Observable<IVehicleTrimsResult> {
		return this.vehicleApiService.getVehicleTrims(year, makeId, modelId).pipe(
			tap((rsp) => {
				this.trims = rsp.data;
			})
		);
	}

	private addCustomValidators(): void {
		this.formGroup.get('year').setValidators([Validators.required, this.invalidYearValidator()]);
		this.formGroup.get('year').updateValueAndValidity();

		this.formGroup.get('makeId').setValidators([Validators.required, this.invalidMakeIdValidator()]);
		this.formGroup.get('makeId').updateValueAndValidity();

		this.formGroup.get('modelId').setValidators([Validators.required, this.invalidModelIdValidator()]);
		this.formGroup.get('modelId').updateValueAndValidity();

		this.formGroup.get('trimId').setValidators([Validators.required, this.invalidTrimIdValidator()]);
		this.formGroup.get('trimId').updateValueAndValidity();

		this.formGroup
			.get('odometer')
			.setValidators([Validators.required, numberOnlyValidator(), this.maxMileageValidator()]);
		this.formGroup.get('odometer').updateValueAndValidity();
	}

	maxMileageValidator(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			const val = control.value;
			if (val >= CONFIRMED_MILEAGE) {
				return { unsupportedMileage: true };
			}
			return null;
		};
	}

	invalidMakeIdValidator(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			if (control?.value && this.inValidVehicleMakeIds.includes(control?.value)) {
				return { unsupportedMake: true };
			}
			return null;
		};
	}

	invalidModelIdValidator(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			const isUnsupportedMake = Boolean(this.formGroup.get('makeId')?.errors?.unsupportedMake);
			if (isUnsupportedMake || (control?.value && this.inValidVehicleModelIds.includes(control?.value))) {
				return { unsupportedModel: true };
			}
			return null;
		};
	}

	invalidTrimIdValidator(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			const isUnsupportedModel = Boolean(this.formGroup.get('modelId')?.errors?.unsupportedModel);
			if (isUnsupportedModel || (control?.value && this.inValidVehicleTrimIds.includes(control?.value))) {
				return { unsupportedTrim: true };
			}
			return null;
		};
	}

	invalidYearValidator(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			const val = control.value;
			const currYear = new Date().getFullYear();
			if (val && currYear - val > MAX_YEAR) {
				return { unsupportedYear: true };
			}
			return null;
		};
	}
}
